import React, { useEffect, useRef, useState, useCallback } from "react";
import svgUrl from "../../../../assets/icons/Tamilnadu-Assembly-NEW.svg";
import styles from "./Map.module.css";
import { constituencyToDistrict, districtColors, constituencyNames, constituencyUrls } from "./constituencyData";

// Decode Adobe Illustrator hex-encoded SVG IDs.
// AI encodes IDs starting with digits as: _x<hex>_<rest>
// e.g. _x31_ = "1", _x31_0 = "10", _x31_00 = "100", _x32_34 = "234"
const getConstituencyNumber = id => {
  if (!id) return null;

  // Skip variant/duplicate IDs like _x31_1_2_ (suffix _<digit>_)
  if (/_\d+_$/.test(id)) return null;

  // Must start with _x (hex-encoded first character)
  if (!id.startsWith("_x")) return null;

  // Replace hex-encoded prefix: _x<HH>_ -> decoded char
  const decoded = id.replace(/^_x([0-9A-Fa-f]{2})_/, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

  // The result should be purely numeric for a valid constituency
  if (/^\d+$/.test(decoded)) {
    return parseInt(decoded, 10);
  }
  return null;
};

const ElectionMapComponent = () => {
  const mapRef = useRef(null);
  const [hoveredDistrict, setHoveredDistrict] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [hoveredConstituency, setHoveredConstituency] = useState(null);
  const useElementsRef = useRef(new Map()); // district -> [use elements]
  const originalViewBoxRef = useRef(null);
  const [svgLoaded, setSvgLoaded] = useState(false);

  // Load SVG file and inject into DOM
  useEffect(
    () => {
      (async () => {
        try {
          const response = await global.fetch(svgUrl);
          const svgText = await response.text();
          if (mapRef.current) {
            mapRef.current.innerHTML = svgText;
            setSvgLoaded(true);
          }
        } catch (err) {
          console.error("Failed to load SVG:", err);
        }
      })();
    },
    [svgUrl]
  );

  // Color constituencies by district on mount (runs after SVG loads)
  useEffect(
    () => {
      if (!svgLoaded || !mapRef.current) return;

      const svgElement = mapRef.current.querySelector("svg");
      if (!svgElement) return;

      // Store original viewBox
      originalViewBoxRef.current = svgElement.getAttribute("viewBox");

      // Hide title block and legend
      const titleBlock = svgElement.querySelector("#Assembly_type");
      if (titleBlock) titleBlock.style.display = "none";
      const legendBlock = svgElement.querySelector("#Layer_16");
      if (legendBlock) legendBlock.style.display = "none";

      // Hide constituency number labels but keep district name labels
      svgElement.querySelectorAll("text").forEach(t => {
        const content = t.textContent.trim();
        if (/^\d+$/.test(content)) {
          t.style.display = "none";
        }
      });

      // Color all symbols directly by iterating through them
      svgElement.querySelectorAll("symbol").forEach(symbol => {
        const symbolId = symbol.getAttribute("id");
        const num = getConstituencyNumber(symbolId);
        if (num && constituencyToDistrict[num]) {
          const district = constituencyToDistrict[num];
          const color = districtColors[district];
          if (color) {
            // Color ALL fill-able shapes inside the symbol
            symbol.querySelectorAll("polygon, path, rect, circle, ellipse").forEach(shape => {
              const currentFill = shape.getAttribute("fill");
              if (currentFill && currentFill !== "none") {
                shape.setAttribute("fill", color);
              }
            });
          }
        }
      });

      // Build district → use elements map
      const districtMap = new Map();
      svgElement.querySelectorAll("use").forEach(el => {
        const href = el.getAttribute("href") || el.getAttribute("xlink:href");
        if (!href || !href.startsWith("#")) return;

        const symbolId = href.substring(1);
        const num = getConstituencyNumber(symbolId);

        if (num && constituencyToDistrict[num]) {
          const district = constituencyToDistrict[num];
          el.setAttribute("data-district", district);
          el.setAttribute("data-constituency", num);
          el.classList.add("map-entity");

          if (!districtMap.has(district)) {
            districtMap.set(district, []);
          }
          districtMap.get(district).push(el);
        }
      });

      useElementsRef.current = districtMap;
    },
    [svgLoaded]
  );

  const clearHighlights = useCallback(() => {
    useElementsRef.current.forEach(elements => {
      elements.forEach(el => {
        el.style.filter = "";
        el.style.opacity = "";
      });
    });
  }, []);

  const highlightDistrict = useCallback(districtName => {
    useElementsRef.current.forEach((elements, district) => {
      elements.forEach(el => {
        if (district === districtName) {
          el.style.filter = "brightness(1.2) saturate(1.5)";
          el.style.opacity = "1";
        } else {
          el.style.filter = "";
          el.style.opacity = "0.4";
        }
      });
    });
  }, []);

  const zoomToDistrict = useCallback(districtName => {
    const svgElement = mapRef.current.querySelector("svg");
    if (!svgElement) return;

    const elements = useElementsRef.current.get(districtName);
    if (!elements || elements.length === 0) return;

    // Calculate combined bounding box
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    elements.forEach(el => {
      try {
        const bbox = el.getBBox();
        minX = Math.min(minX, bbox.x);
        minY = Math.min(minY, bbox.y);
        maxX = Math.max(maxX, bbox.x + bbox.width);
        maxY = Math.max(maxY, bbox.y + bbox.height);
      } catch (e) {
        /* ignore */
      }
    });

    if (minX === Infinity) return;

    // Add padding
    const padding = Math.max(maxX - minX, maxY - minY) * 0.15;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Highlight selected district, dim others (keep all visible)
    useElementsRef.current.forEach((elems, district) => {
      elems.forEach(el => {
        if (district === districtName) {
          el.style.filter = "brightness(1.2) saturate(1.5)";
          el.style.opacity = "1";
          el.style.stroke = "#333";
          el.style.strokeWidth = "0.5";
        } else {
          el.style.filter = "";
          el.style.opacity = "0.3";
        }
      });
    });
    setSelectedDistrict(districtName);
    setHoveredDistrict(null);
  }, []);

  const zoomOut = useCallback(() => {
    const svgElement = mapRef.current.querySelector("svg");
    if (!svgElement || !originalViewBoxRef.current) return;

    // Restore all elements
    useElementsRef.current.forEach(elements => {
      elements.forEach(el => {
        el.style.filter = "";
        el.style.opacity = "";
        el.style.stroke = "";
        el.style.strokeWidth = "";
      });
    });
    setSelectedDistrict(null);
    setHoveredConstituency(null);
  }, []);

  const handleMouseMove = e => {
    const target = e.target.closest("[data-district]");
    if (!target) {
      if (hoveredDistrict && !selectedDistrict) {
        setHoveredDistrict(null);
        clearHighlights();
      }
      if (selectedDistrict) {
        setHoveredConstituency(null);
      }
      return;
    }

    const rect = mapRef.current.getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left + 15,
      y: e.clientY - rect.top + 15
    });

    if (selectedDistrict) {
      const num = parseInt(target.getAttribute("data-constituency"), 10);
      if (num !== hoveredConstituency) {
        setHoveredConstituency(num);
      }
    } else {
      const district = target.getAttribute("data-district");
      if (district !== hoveredDistrict) {
        setHoveredDistrict(district);
        highlightDistrict(district);
      }
    }
  };

  const handleMouseLeave = () => {
    if (!selectedDistrict) {
      setHoveredDistrict(null);
      clearHighlights();
    }
    setHoveredConstituency(null);
  };

  const handleClick = e => {
    const target = e.target.closest("[data-district]");
    if (!target) return;

    if (selectedDistrict) return;

    const district = target.getAttribute("data-district");
    if (district) {
      zoomToDistrict(district);
    }
  };

  const getDistrictConstituencies = () => {
    if (!selectedDistrict) return [];
    const result = [];
    for (const [num, district] of Object.entries(constituencyToDistrict)) {
      if (district === selectedDistrict) {
        result.push({
          number: parseInt(num, 10),
          name: constituencyNames[num] || `Constituency ${num}`,
          url: constituencyUrls[num]
        });
      }
    }
    return result.sort((a, b) => a.number - b.number);
  };

  const tooltipText = selectedDistrict
    ? hoveredConstituency
      ? `#${hoveredConstituency} - ${constituencyNames[hoveredConstituency] || ""}`
      : null
    : hoveredDistrict;

  return (
    <div className={styles["map-wrapper"]}>
      <div
        className={`${styles["map-container"]} ${selectedDistrict ? styles["detail-view"] : ""}`}
        ref={mapRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {/* SVG loaded dynamically via useEffect */}

        {tooltipText && (
          <div className={styles["tooltip"]} style={{ left: tooltipPos.x, top: tooltipPos.y }}>
            {tooltipText}
          </div>
        )}
      </div>

      {selectedDistrict && (
        <div className={styles["detail-panel"]}>
          <button className={styles["back-button"]} onClick={zoomOut}>
            ← Back to full map
          </button>
          <h2 className={styles["district-title"]}>{selectedDistrict}</h2>
          <p className={styles["constituency-count"]}>{getDistrictConstituencies().length} constituencies</p>
          <ul className={styles["constituency-list"]}>
            {getDistrictConstituencies().map(c => (
              <li
                key={c.number}
                className={`${styles["constituency-item"]} ${hoveredConstituency === c.number ? styles["active"] : ""}`}
              >
                <a href={c.url} target="_blank" rel="noopener noreferrer" className={styles["constituency-link"]}>
                  <span className={styles["constituency-number"]}>#{c.number}</span>
                  <span className={styles["constituency-name"]}>{c.name}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ElectionMapComponent;
